import { apiService } from './api';
import { User, LoginFormData } from '../types';

interface TokenValidityResponse {
  isValid: boolean;
  needsRefresh: boolean;
  reason?: 'PERMISSIONS_UPDATED' | 'SUBSCRIPTION_EXPIRED';
  message?: string;
}

interface RefreshTokenResponse {
  token: string;
  user: User;
}

export const authService = {
  // Login user - supports both email and userCode
  login: async (credentials: LoginFormData) => {
    const response = await apiService.post<{ token: string; user: User }>('/auth/login', {
      identifier: credentials.identifier,
      password: credentials.password
    });
    return response.data || { token: '', user: {} as User };
  },

  // Get current user profile
  getProfile: async () => {
    const response = await apiService.get<User>('/auth/profile');
    return response.data;
  },

  // Update user profile
  updateProfile: async (data: { name: string; email?: string }) => {
    const response = await apiService.put<User>('/auth/profile', data);
    return response.data;
  },

  // Upload school logo
  uploadSchoolLogo: async (file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await apiService.upload<{ logo: string }>('/admin/logo', formData);
    return response.data;
  },

  // Change password
  changePassword: async (data: { currentPassword: string; newPassword: string }) => {
    const response = await apiService.put('/auth/change-password', data);
    return response.data;
  },

  // Logout user
  logout: async () => {
    const response = await apiService.post('/auth/logout');
    return response.data;
  },

  // Check if token is still valid (permissions/subscription haven't changed)
  checkToken: async (): Promise<TokenValidityResponse | null> => {
    const response = await apiService.get<TokenValidityResponse>('/auth/check-token');
    return response.data || null;
  },

  // Refresh token with updated permissions and subscription data
  refreshToken: async (): Promise<RefreshTokenResponse | null> => {
    const response = await apiService.post<RefreshTokenResponse>('/auth/refresh-token');
    return response.data || null;
  },
}; 